import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LambdaService } from 'src/app/lambda.service';
import { SettingsService } from 'src/app/settings.service';
import { ThemingService } from 'src/app/theming.service';
import {
  ShopCompany,
  ShopCompanyPayload,
  ShopCompanyThemeFormChanges,
  ShopCompanyUpdatePayload,
} from '../../shop-companies/shop-company.models';

@Component({
  selector: 'app-shop-company-form',
  templateUrl: './shop-company-form.component.html',
  styleUrls: ['./shop-company-form.component.scss'],
})
export class ShopCompanyFormComponent implements OnInit {
  @Input() embedded = false;
  @Input() shopCompanyId: number | null = null;
  companyForm: FormGroup;
  shopCompany: ShopCompany | null = null;
  activeTab: 'general' | 'theme' = 'general';
  loading = false;
  saving = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private fb: FormBuilder,
    private lambdaService: LambdaService,
    private settingsService: SettingsService,
    public themingService: ThemingService
  ) {
    this.companyForm = this.fb.group({
      companyName: ['', [Validators.required, Validators.maxLength(150)]],
      companyLegalName: ['', [Validators.required, Validators.maxLength(200)]],
      companyPhone: ['', [Validators.required, Validators.maxLength(50)]],
      companyWebsiteUrl: [
        '',
        [
          Validators.required,
          Validators.pattern(/^https?:\/\/.+/i),
          Validators.maxLength(500),
        ],
      ],
      status: [1, Validators.required],
    });
  }

  ngOnInit(): void {
    const id = Number(
      this.shopCompanyId || this.settingsService.getEditingShopCompany()
    );
    if (Number.isFinite(id) && id > 0) {
      this.shopCompanyId = id;
      this.loadCompany();
    }
  }

  get isEditMode(): boolean {
    return this.shopCompanyId !== null;
  }

  async loadCompany(): Promise<void> {
    if (!this.shopCompanyId) {
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    try {
      const response: any = await this.lambdaService.getShopCompanyById(
        this.themingService.shopId,
        this.shopCompanyId
      );
      const company = this.extractCompany(response);
      this.shopCompany = company;
      this.companyForm.patchValue({
        companyName: company.companyName,
        companyLegalName: company.companyLegalName,
        companyPhone: company.companyPhone,
        companyWebsiteUrl: company.companyWebsiteUrl,
        status: Number(company.status),
      });
    } catch (error) {
      console.error('Error loading shop company:', error);
      this.errorMessage = 'SHOP_COMPANY_LOAD_ERROR';
    } finally {
      this.loading = false;
    }
  }

  async saveGeneralData(): Promise<void> {
    if (this.companyForm.invalid) {
      this.companyForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';
    const payload = this.companyForm.getRawValue() as ShopCompanyPayload;

    try {
      if (this.shopCompanyId) {
        const fullPayload = this.buildUpdatePayload(payload);
        await this.lambdaService.updateShopCompany(
          this.themingService.shopId,
          this.shopCompanyId,
          fullPayload
        );
        this.shopCompany = { ...fullPayload };
        this.successMessage = 'SHOP_COMPANY_UPDATED';
      } else {
        const response: any = await this.lambdaService.createShopCompany(
          this.themingService.shopId,
          payload
        );
        const created = this.extractCompany(response);
        const createdId =
          created?.shopCompanyId ??
          response?.shopCompanyId ??
          response?.data?.shopCompanyId;
        if (!createdId) {
          throw new Error('The API did not return shopCompanyId');
        }
        this.shopCompanyId = Number(createdId);
        this.shopCompany = {
          ...created,
          ...payload,
          shopCompanyId: this.shopCompanyId,
          shopId: created?.shopId ?? this.themingService.shopId,
        };
        this.settingsService.setListingShopCompanies();
        return;
      }
    } catch (error) {
      console.error('Error saving shop company:', error);
      this.errorMessage = 'SHOP_COMPANY_SAVE_ERROR';
    } finally {
      this.saving = false;
    }
  }

  selectTab(tab: 'general' | 'theme'): void {
    if (tab === 'theme' && !this.shopCompanyId) {
      return;
    }
    this.activeTab = tab;
  }

  backToList(): void {
    this.settingsService.setListingShopCompanies();
  }

  async saveTheme(themeChanges: ShopCompanyThemeFormChanges): Promise<void> {
    if (!this.shopCompanyId || !this.shopCompany) {
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';
    try {
      const fullPayload = this.buildUpdatePayload(themeChanges);
      await this.lambdaService.updateShopCompany(
        this.themingService.shopId,
        this.shopCompanyId,
        fullPayload
      );
      this.shopCompany = { ...fullPayload };
      this.successMessage = 'SHOP_COMPANY_THEME_UPDATED';
    } catch (error) {
      console.error('Error saving shop company theme:', error);
      this.errorMessage = 'SHOP_COMPANY_THEME_SAVE_ERROR';
    } finally {
      this.saving = false;
    }
  }

  private buildUpdatePayload(
    changes: ShopCompanyPayload | ShopCompanyThemeFormChanges
  ): ShopCompanyUpdatePayload {
    if (!this.shopCompanyId || !this.shopCompany) {
      throw new Error('Shop company must be loaded before updating');
    }

    const draft = {
      ...this.shopCompany,
      ...this.companyForm.getRawValue(),
      ...changes,
    };

    return {
      shopCompanyId: this.shopCompanyId,
      shopId: draft.shopId ?? this.themingService.shopId,
      companyName: draft.companyName,
      companyLegalName: draft.companyLegalName,
      companyPhone: draft.companyPhone,
      companyWebsiteUrl: draft.companyWebsiteUrl,
      status: Number(draft.status),
      useParentTheme: this.normalizeUseParentTheme(draft.useParentTheme),
      shopFavicon: draft.shopFavicon ?? null,
      shopLogoWhite: draft.shopLogoWhite ?? null,
      shopLogoLarge: draft.shopLogoLarge ?? null,
      shopLogoSmall: draft.shopLogoSmall ?? null,
      shopLogoMainColor: draft.shopLogoMainColor ?? null,
      shopLogoMainColorLight: draft.shopLogoMainColorLight ?? null,
      shopLogoMainColorDark: draft.shopLogoMainColorDark ?? null,
      shopLogoMainColorTitleActive:
        draft.shopLogoMainColorTitleActive ?? null,
      shopLogoMainColorTitlePlaceholder:
        draft.shopLogoMainColorTitlePlaceholder ?? null,
      headerBackground: draft.headerBackground ?? null,
      footerBackground: draft.footerBackground ?? null,
      shopFontFamily1: draft.shopFontFamily1 ?? null,
      shopFontFamily2: draft.shopFontFamily2 ?? null,
      showSearchBar: draft.showSearchBar ?? null,
      showAuthButtons: draft.showAuthButtons ?? null,
      showCart: draft.showCart ?? null,
      languageSelectorPosition: draft.languageSelectorPosition ?? null,
    };
  }

  private extractCompany(response: any): ShopCompany {
    const company = (
      response?.data?.shopCompany ??
      response?.shopCompany ??
      response?.data ??
      response
    ) as ShopCompany;
    return {
      ...company,
      useParentTheme: this.normalizeUseParentTheme(company.useParentTheme),
      showSearchBar: this.normalizeBoolean(company.showSearchBar),
      showAuthButtons: this.normalizeBoolean(company.showAuthButtons),
      showCart: this.normalizeBoolean(company.showCart),
    };
  }

  private normalizeUseParentTheme(value: any): boolean {
    return value === true || value === 1 || value === '1';
  }

  private normalizeBoolean(value: any): boolean | null {
    if (value === null || value === undefined) {
      return null;
    }
    return value === true || value === 1 || value === '1';
  }
}
